import { useEffect, useRef } from "react";

const ACCORDION_BUTTON_SELECTOR = "button[aria-expanded]";
const ACCORDION_ITEM_SELECTOR = "[class*='accordionItemContainer']";

/**
 * Custom hook to manage automatic scrolling of accordion items
 * when clicked, if they are not visible in the viewport.
 *
 * @param enabled - Whether to enable the scroll behavior (default: true)
 * @returns Ref to be attached to the accordion container
 */
export function useAccordionScroll(enabled = true) {
  const containerRef = useRef<HTMLElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const handleClick = (event: MouseEvent) => {
      const button = (event.target as Element).closest(
        ACCORDION_BUTTON_SELECTOR
      );
      if (!button) return;

      const accordionItem = button.closest(ACCORDION_ITEM_SELECTOR);
      if (!accordionItem) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = globalThis.setTimeout(() => {
        const rect = accordionItem.getBoundingClientRect();
        const isVisible =
          rect.top >= 0 &&
          rect.bottom <= globalThis.innerHeight &&
          rect.left >= 0 &&
          rect.right <= window.innerWidth;

        if (!isVisible) {
          accordionItem.scrollIntoView({
            behavior: "instant",
            block: "start",
          });
        }
      }, 50);
    };

    container.addEventListener("click", handleClick);
    return () => {
      container.removeEventListener("click", handleClick);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [enabled]);

  return containerRef;
}
